import { useEffect, useRef, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { GridPaginationModel, GridRowId } from '@mui/x-data-grid';
import { Product } from '@prisma/client';
import { FormikValues } from 'formik';

import { navigate } from '@redwoodjs/router';
import { useMutation, useQuery } from '@redwoodjs/web';

import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { ProductType } from 'src/constants';
import {
    CREATE_PRICING_OFFER_MUTATION,
    UPDATE_PRICING_OFFER_MUTATION,
} from 'src/graphql/mutations/pricing.mutation';
import {
    FIND_SECONDARY_PRODUCTS_QUERY,
    GET_PRICING_OFFER,
    GET_PRICING_OFFERS_BY_TYPE,
} from 'src/graphql/queries/pricing.query';
import { FIND_PRIMARY_PRODUCT_MUTATION } from 'src/graphql/queries/product.query';
import { LOGGED_IN_USER_QUERY } from 'src/graphql/queries/users.query';
import { productPricingValidation } from 'src/validation/PricingValidation';

import {
    CretePricingOfferInput,
    FilterInputs,
    OfferStatus,
    PricingLimit,
    PricingTerm,
    PricingType,
    ProductCatalogue,
    SalesConditionsType,
    SelectedProducts,
    TerminalOfferType,
    pricingTypes,
} from './index';

const defaultInitialValues = {
    name: '',
    term: null,
    type: pricingTypes.ONE_OFF,
    customTerm: null,
    primaryProductIds: null,
    secondaryProductIds: null,
    salesConditions: SalesConditionsType.NO,
    gpvConditions: SalesConditionsType.NO,
    mccConditions: SalesConditionsType.NO,
    gpvLowest: null,
    gpvHighest: null,
    allPartners: SalesConditionsType.NO,
    mccs: [],
};

export const usePricingsOffers = (id: number, pricingType: PricingType, isUpdate: boolean) => {
    const timeout = useRef(null);
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 5,
        page: 0,
    });
    const [primaryProduct, setPrimaryProduct] = useState({});
    const [primaryProductList, setPrimaryProductList] = useState([]);
    const [selectedPrimaryProductIds, setSelectedPrimaryProductIds] = useState([]);
    const [showCustomTerm, setShowCustomTerm] = useState(false);
    const [secondaryProductList, setSecondaryProductList] = useState([]);
    const [selectedSecondaryProductIds, setSelectedSecondaryProductIds] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState<SelectedProducts[]>([]);
    const [defaultProducts, setDefaultProducts] = useState<SelectedProducts[]>([]);
    const [expanded, setExpanded] = useState<string | false>(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedPartners, setSelectedPartners] = useState([]);
    const [open, setOpenModal] = useState(false);
    const [initialValues, setInitialValues] = useState(defaultInitialValues);
    const [rowCountState, setRowCountState] = useState(0);
    const [fieldIds, setFieldIds] = useState([]);
    const mapPageToNextCursor = useRef<{ [page: number]: GridRowId }>({});

    const [findPrimaryProducts] = useLazyQuery(FIND_PRIMARY_PRODUCT_MUTATION);
    const [findSecondaryProducts] = useLazyQuery(FIND_SECONDARY_PRODUCTS_QUERY);
    const [getLoggedInUser] = useLazyQuery(LOGGED_IN_USER_QUERY);

    const [openSaveModal, setOpenSaveModal] = useState(false);
    const [formValues, setFormValues] = useState<FormikValues>();
    const [showEditProduct, setShowEditProduct] = useState(true);
    const [disabledOfferName, setDisabledOfferName] = useState(false);

    const [filterInputs, setFilterInputs] = useState<FilterInputs>({
        type: pricingType,
    });
    const [selectedOffer, setSelectedOffer] = useState<TerminalOfferType>();

    const { data, loading, refetch } = useQuery(GET_PRICING_OFFERS_BY_TYPE, {
        variables: { input: { ...filterInputs, type: pricingType } },
        fetchPolicy: 'network-only',
    });

    const [getTerminalPricingOffer, { loading: loading2 }] = useLazyQuery(GET_PRICING_OFFER);

    const [createPricingOffer, { loading: loadingCreatePricingOffer }] = useMutation(
        CREATE_PRICING_OFFER_MUTATION,
        {
            onCompleted: async ({ createPricingOffer }) => {
                const { name, type } = createPricingOffer;
                // After adding product offering, clear local storage
                localStorage.removeItem('selectedProducts');
                showSuccessToast(`${name} pricing created`);
                timeout.current = setTimeout(() => {
                    navigate(`/${type.toLowerCase()}-pricing`);
                }, 1000);
            },
            refetchQueries: [
                {
                    query: GET_PRICING_OFFERS_BY_TYPE,
                    variables: {
                        input: { type: pricingType },
                        page: { page: 0, pageSize: 5 },
                    },
                },
            ],
            onError: (error) => {
                showErrorToast(error.message);
            },
        }
    );

    const [updatePricingOffer, { loading: loadingUpdatePricingOffer }] = useMutation(
        UPDATE_PRICING_OFFER_MUTATION,
        {
            onCompleted: ({ updatePricingOffer }) => {
                const { name, type, status } = updatePricingOffer;
                const mapStatus = {
                    ACTIVE: 'created',
                    DELETED: 'deleted',
                    DEACTIVATED: 'deactivated',
                };
                // After Editing product offering, clear local storage
                localStorage.removeItem('selectedProducts');
                showSuccessToast(`${name} ${type.toLowerCase()} pricing ${mapStatus[status]}`);
                timeout.current = setTimeout(() => {
                    return navigate(`/${type.toLowerCase()}-pricing`);
                }, 1000);
            },
            refetchQueries: [
                {
                    query: GET_PRICING_OFFERS_BY_TYPE,
                    variables: {
                        input: { type: pricingType },
                        page: { page: 0, pageSize: 5 },
                    },
                },
            ],
            onError: (error) => {
                showErrorToast(error.message);
            },
        }
    );

    const getLoggedInUserData = async () => {
        const loggedInUser = await getLoggedInUser();
        if (loggedInUser.data.loggedInUser) {
            const user = loggedInUser?.data?.loggedInUser;
            if (user?.partner) {
                const partner = { id: user?.partner?.id, name: null };
                setSelectedPartners([partner]);
            }
        }
    };

    useEffect(() => {
        const makeRequest = () => {
            return getLoggedInUserData();
        };
        makeRequest();
    }, []);

    useEffect(() => {
        return () => {
            return clearTimeout(timeout.current);
        };
    }, [timeout]);

    const onSubmit = async (input: CretePricingOfferInput | FormikValues) => {
        const storedSelectedProducts = JSON.parse(localStorage.getItem('selectedProducts') || '[]');

        const productPromo = storedSelectedProducts.map(({ id, shouldApplyPromo }) => {
            return {
                productId: id,
                shouldApplyPromo: shouldApplyPromo === SalesConditionsType.YES,
            };
        });

        const pricing = storedSelectedProducts.map(({ pricing }) => {
            const { catalogue, type, oneOff, monthly, options, productId } = pricing;
            return {
                productId,
                type,
                catalogue,

                oneOff: {
                    lowest: oneOff.lowest,
                    highest: oneOff.highest,
                },
                monthly: {
                    lowest: monthly.lowest,
                    highest: monthly.highest,
                },
                options: {
                    oneOff: options.oneOff.map(({ sales, commission }) => {
                        return {
                            sales: sales || 0,
                            commission: commission || 0,
                        };
                    }),
                    monthly: options.monthly.map(({ sales, commission }) => {
                        return {
                            sales: sales || 0,
                            commission: commission || 0,
                        };
                    }),
                },
            };
        });

        if (selectedPrimaryProductIds.length === 0) {
            setErrorMessage(`Products for ${pricingType.toLowerCase()} pricing is required`);
            return;
        }
        const { mccs, gpvConditions, mccConditions, customTerm, ...rest } = input;
        let data = {
            ...rest,
            pricing,
            productPromo,
            customTerm: Number(customTerm),
            partnerIds: selectedPartners.map(({ id }) => {
                return parseInt(id);
            }),
            mccIds: null,
        };
        if (selectedPrimaryProductIds.length >= 1) {
            data = { ...data, primaryProductIds: selectedPrimaryProductIds };
        }
        if (selectedSecondaryProductIds) {
            data = { ...data, secondaryProductIds: selectedSecondaryProductIds };
        }

        if (!isUpdate) {
            await createPricingOffer({
                variables: {
                    input: {
                        ...data,
                        type: pricingType,
                        mccIds:
                            mccConditions === SalesConditionsType.YES
                                ? mccs.map(({ key }) => {
                                      return parseInt(key);
                                  })
                                : null,
                        gpvLowest:
                            gpvConditions === SalesConditionsType.YES
                                ? Number(rest.gpvLowest)
                                : null,
                        gpvHighest:
                            gpvConditions === SalesConditionsType.YES
                                ? Number(rest.gpvHighest)
                                : null,
                    },
                },
            });
        } else {
            await updatePricingOffer({
                variables: {
                    input: {
                        ...data,
                        mccIds:
                            mccConditions === SalesConditionsType.YES
                                ? mccs.map(({ key }) => {
                                      return parseInt(key);
                                  })
                                : null,
                        gpvLowest:
                            gpvConditions === SalesConditionsType.YES
                                ? Number(rest.gpvLowest)
                                : null,
                        gpvHighest:
                            gpvConditions === SalesConditionsType.YES
                                ? Number(rest.gpvHighest)
                                : null,
                    },
                },
            });
        }
    };

    const validationSchema = () => {
        return productPricingValidation(selectedPrimaryProductIds, fieldIds);
    };

    const updatePricing = (productId: number, catalogue: ProductCatalogue) => {
        const multiplePrices = new Array(3).fill({ sales: null, commission: null });
        const price = {
            productId,
            catalogue,
            type: 'One-off',
            disabled: `monthly-${productId}`,
            oneOff: {
                lowest: null,
                highest: null,
            },
            monthly: {
                lowest: null,
                highest: null,
            },
            options: {
                oneOff: multiplePrices,
                monthly: multiplePrices,
            },
        };
        return price;
    };

    const handleSetSelectContractTerm = (value: string) => {
        if (value === PricingTerm.CUSTOM) {
            setShowCustomTerm(true);
        } else {
            setShowCustomTerm(false);
        }
    };

    const handleFindPrimaryProducts = async ({ page }) => {
        const products = await findPrimaryProducts({
            variables: { name: primaryProduct, type: pricingType, page },
        });
        if (products) {
            const { total, page, pageSize, data } = products?.data?.findPrimaryProduct;

            // Add the new searched for products but keep selected products in table
            setPrimaryProductList([
                ...data.filter(({ id }) => {
                    return !selectedProducts
                        .map(({ id }) => {
                            return id;
                        })
                        .includes(id);
                }),
                ...selectedProducts,
            ]);
            setRowCountState(total);
            setPaginationModel({ page, pageSize });
            setErrorMessage('');
            if (!products.data.findPrimaryProduct?.data?.length) {
                showErrorToast('No record found');
            }
        }
    };

    const updateSelectedPrimaryProductIds = (newSelectedProducts) => {
        const primaryProductIds = newSelectedProducts
            .filter((product) => {
                return product.catalogue === 'PRIMARY';
            })
            .map((product) => {
                return product.id;
            });
        setSelectedPrimaryProductIds(primaryProductIds);
    };

    const handleSelectedPrimaryProductsIds = async (ids: GridRowId[]) => {
        const newId =
            ids.find((i) => {
                return !selectedPrimaryProductIds.includes(i);
            }) || null;

        if (newId) {
            // Add selected product
            const product = primaryProductList.find((product) => {
                return product.id === newId;
            });
            if (product) {
                const updateProductWithDefaultPricing = {
                    ...product,
                    shouldApplyPromo: product?.shouldApplyPromo ?? SalesConditionsType.NO,
                    pricing: updatePricing(product.id, product.catalogue),
                };

                const updatedProducts = [...selectedProducts, updateProductWithDefaultPricing];
                setSelectedProducts(updatedProducts);
                updateSelectedPrimaryProductIds(updatedProducts);
            }
        } else {
            // Find deselected primary product id
            const deselectedPrimaryId = selectedPrimaryProductIds.find((i) => {
                return !ids.includes(i);
            });

            if (deselectedPrimaryId !== undefined) {
                const product = selectedProducts.find((product) => {
                    return product.id === deselectedPrimaryId;
                });
                const secondaryProductIds = product?.secondaryProductIds || [];
                const deselectedIds = [deselectedPrimaryId, ...secondaryProductIds];

                if (deselectedIds.length > 0) {
                    const updatedProducts = selectedProducts.filter((product) => {
                        return !deselectedIds.includes(product.id);
                    });

                    setSelectedProducts(updatedProducts);
                    updateSelectedPrimaryProductIds(updatedProducts);
                }
            }
        }

        const secondaryProducts = await findSecondaryProducts({ variables: { ids } });
        if (secondaryProducts) {
            const productList = secondaryProducts.data.findSecondaryProducts;
            setSecondaryProductList(productList);
        }
    };

    const updateSelectedSecondaryProductIds = (newSelectedProducts) => {
        const secondaryProductIds = newSelectedProducts
            .filter((product) => {
                return product.catalogue === 'SECONDARY';
            })
            .map((product) => {
                return product.id;
            });
        setSelectedSecondaryProductIds(secondaryProductIds);
    };

    const handleSelectedSecondaryProductsIds = async (ids: GridRowId[]) => {
        // Find newly selected secondary product ID
        const newId =
            ids.find((i) => {
                return !selectedSecondaryProductIds.includes(i);
            }) || null;

        if (newId) {
            // Add selected product
            const product = secondaryProductList.find((product) => {
                return product.id === newId;
            });
            if (product) {
                const updateProductWithDefaultPricing = {
                    ...product,
                    shouldApplyPromo: isUpdate ? product?.shouldApplyPromo : SalesConditionsType.NO,
                    pricing: updatePricing(product.id, product.catalogue),
                };

                const productInSelection = selectedProducts.find((selected) => {
                    return selected.id === product.id;
                });
                if (!productInSelection) {
                    const updatedProducts = [...selectedProducts, updateProductWithDefaultPricing];
                    setSelectedProducts(updatedProducts);
                    updateSelectedSecondaryProductIds(updatedProducts);
                }
            }
        } else {
            // Find deselected secondary product ID
            const deselectedSecondaryId = selectedSecondaryProductIds.find((i) => {
                return !ids.includes(i);
            });

            if (deselectedSecondaryId !== undefined) {
                const updatedSelectedProducts = selectedProducts.filter((product) => {
                    return product.id !== deselectedSecondaryId;
                });

                setSelectedProducts(updatedSelectedProducts);
                updateSelectedSecondaryProductIds(updatedSelectedProducts);
            }
        }
    };

    const handleAccordionChange = (panel: string) => {
        return (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : false);
        };
    };

    const handlePricingTypeOnChange = (value: string, id: string) => {
        if (!isUpdate) {
            const product = selectedProducts.find((product) => {
                return product.id === parseInt(id);
            });

            const { pricing } = product;
            const mapDisabledState = {
                'One-off': 'monthly',
                Monthly: 'oneOff',
                Both: null,
            };

            const updatedPricing = {
                ...pricing,
                type: value,
                disabled: `${mapDisabledState[value]}-${id}`,
            };
            const updatedProduct = { ...product, pricing: updatedPricing };
            const updatedProducts = selectedProducts.map((product) => {
                return product.id === parseInt(id) ? updatedProduct : product;
            });
            setSelectedProducts(updatedProducts);
        } else {
            const index = selectedProducts.findIndex((product) => {
                return product.id === parseInt(id);
            });
            const product = selectedProducts[index];
            const { pricing } = product;
            const updatedPricing = { ...pricing, type: value };
            const updatedProduct = { ...product, pricing: updatedPricing };
            selectedProducts[index] = updatedProduct;
            setSelectedProducts(selectedProducts);
        }
    };

    const handlePricingLimitChange = (limit: string, updatedLimit: number, id: number) => {
        const product = selectedProducts.find((product) => {
            return product.id === id;
        });
        const { pricing } = product;

        let updatedPricng = pricing;
        if (limit === PricingLimit.oneOff.lowest) {
            updatedPricng = {
                ...updatedPricng,
                oneOff: {
                    ...pricing.oneOff,
                    lowest: updatedLimit,
                },
            };
        } else if (limit === PricingLimit.oneOff.highest) {
            updatedPricng = {
                ...updatedPricng,
                oneOff: {
                    ...pricing.oneOff,
                    highest: updatedLimit,
                },
            };
        } else if (limit === PricingLimit.monthly.lowest) {
            updatedPricng = {
                ...updatedPricng,
                monthly: {
                    ...pricing.monthly,
                    lowest: updatedLimit,
                },
            };
        } else if (limit === PricingLimit.monthly.highest) {
            updatedPricng = {
                ...updatedPricng,
                monthly: {
                    ...pricing.monthly,
                    highest: updatedLimit,
                },
            };
        }

        const updatedProduct = { ...product, pricing: updatedPricng };

        const updatedProducts = selectedProducts.map((product) => {
            return product.id === id ? updatedProduct : product;
        });

        setSelectedProducts(updatedProducts);
        localStorage.setItem('selectedProducts', JSON.stringify(updatedProducts));
    };

    const enableProductForPromo = (value: SalesConditionsType, id: string) => {
        const updatedProducts = selectedProducts.map((product) => {
            return product.id === parseInt(id) ? { ...product, shouldApplyPromo: value } : product;
        }) as SelectedProducts[];
        setSelectedProducts(updatedProducts);
    };

    const handlePricingOnChange = (e: any) => {
        e.preventDefault();
        const { name, value } = e.target;
        const productKey = name.split('-');
        const [type, field, id] = productKey;
        const index = selectedProducts.findIndex((product) => {
            return product.id === parseInt(id);
        });
        const product = selectedProducts[index];
        let lowest = null;
        let highest = null;
        if (defaultProducts.length >= 1) {
            ({ lowest, highest } = defaultProducts[index].pricing[type]);
        } else {
            ({ lowest, highest } = product.pricing[type]);
        }

        const { pricing, message } = product;
        const updatedPricing = {
            ...pricing,
            [type]: {
                ...pricing[type],
                [field]: Number(value),
            },
        };
        const updatedProduct = {
            ...product,
            pricing: updatedPricing,
            message: {
                ...message,
                [`${type}-${field}-${id}`]: errorMessage,
            },
        };
        const updatedProducts = selectedProducts.map((product) => {
            return product.id === parseInt(id) ? updatedProduct : product;
        });
        setSelectedProducts(updatedProducts);
    };

    const handleMultiplePricingOnChange = (updatedProduct: Product) => {
        let storedSelectedProducts = JSON.parse(localStorage.getItem('selectedProducts') || '[]');
        if (!storedSelectedProducts || storedSelectedProducts.length === 0) {
            localStorage.setItem('selectedProducts', JSON.stringify([updatedProduct]));
        } else {
            const storedUpdatedProduct = storedSelectedProducts.find(
                (storedSelectedProduct: { id: number }) => {
                    return storedSelectedProduct.id === updatedProduct.id;
                }
            );

            if (!storedUpdatedProduct) {
                if (updatedProduct.type === ProductType.ONLINE) {
                    storedSelectedProducts = [
                        ...storedSelectedProducts,
                        ...[updatedProduct],
                    ].filter(({ type }) => {
                        return type === ProductType.ONLINE;
                    });
                } else if (updatedProduct.type === ProductType.TERMINAL) {
                    storedSelectedProducts = [
                        ...storedSelectedProducts,
                        ...[updatedProduct],
                    ].filter(({ type }) => {
                        return type === ProductType.TERMINAL;
                    });
                }
            }

            const updatedProducts = storedSelectedProducts.map((product: Product) => {
                if (product.id === updatedProduct.id) {
                    const { options, isUpdating } = updatedProduct.pricing;

                    const oneOff =
                        isUpdating === 'oneOff'
                            ? options[isUpdating]
                            : product.pricing?.options.oneOff;
                    const monthly =
                        isUpdating === 'monthly'
                            ? options[isUpdating]
                            : product.pricing?.options.monthly;

                    delete updatedProduct?.isUpdating;

                    return {
                        ...updatedProduct,
                        shouldApplyPromo:
                            updatedProduct?.shouldApplyPromo ?? SalesConditionsType.NO,
                        pricing: {
                            ...updatedProduct.pricing,
                            options: {
                                ...product.pricing.options,
                                oneOff,
                                monthly,
                            },
                        },
                    };
                }
                return product;
            });

            localStorage.setItem('selectedProducts', JSON.stringify(updatedProducts));
        }
    };

    const handleCloseModal = () => {
        setOpenModal((open) => {
            return !open;
        });
    };

    const handleOpenModal = () => {
        const pricing = selectedProducts.map(({ pricing }) => {
            return pricing;
        });
        if (pricing.length === 0) {
            setErrorMessage(`Products for ${pricingType.toLowerCase()} pricing is required`);
            return;
        }
        setOpenModal(true);
    };

    const [getPricingOffer] = useLazyQuery(GET_PRICING_OFFER);

    const getPricingOfferById = async (id: string) => {
        const pricingOffer = await getPricingOffer({ variables: { id } });
        if (pricingOffer.data) {
            const primaryProducts = pricingOffer?.data.offer.primaryProducts;
            const primaryProductIds = primaryProducts.map(({ id }) => {
                return id;
            });
            setPrimaryProductList(primaryProducts);
            setSelectedPrimaryProductIds(primaryProductIds);

            const secondaryProducts = pricingOffer?.data.offer.secondaryProducts;
            const secondaryProductIds = secondaryProducts.map(({ id }) => {
                return id;
            });
            setSecondaryProductList(secondaryProducts);
            setSelectedSecondaryProductIds(secondaryProductIds);

            const { pricings, shouldApplyPromo } = pricingOffer.data.offer;
            const selectedProducts = [...primaryProducts, ...secondaryProducts];

            const selectedProductsWithPricing = selectedProducts.reduce((products, product) => {
                const pricing = pricings.find((pricing: any) => {
                    return pricing.product.id === product.id;
                });
                products = [
                    ...products,
                    {
                        ...product,
                        shouldApplyPromo: shouldApplyPromo ?? SalesConditionsType.NO,
                        pricing: { ...pricing.prices, productId: product.id },
                    },
                ];
                return products;
            }, []);
            setSelectedProducts([...selectedProductsWithPricing]);
            setDefaultProducts([...selectedProductsWithPricing]);

            const {
                term,
                customTerm,
                gpvLowest,
                gpvHighest,
                gpvConditions,
                salesConditions,
                mccConditions,
                mccs,
            } = pricingOffer.data.offer;

            setInitialValues((initialValues) => {
                return {
                    ...initialValues,
                    term,
                    customTerm,
                    gpvConditions,
                    gpvLowest,
                    mccConditions,
                    gpvHighest,
                    salesConditions,
                    mccs: mccs.map(({ id, code, name }) => {
                        return {
                            key: id,
                            value: `${code} - ${name}`,
                        };
                    }),
                    allPartners: SalesConditionsType.NO,
                };
            });
        }
    };

    const handlePaginationModelChange = async (newPaginationModel: GridPaginationModel) => {
        if (
            newPaginationModel.page === 0 ||
            mapPageToNextCursor.current[newPaginationModel.page - 1]
        ) {
            setPaginationModel(newPaginationModel);
        }

        await handleFindPrimaryProducts({ page: newPaginationModel });
    };

    const getTerminalPricing = useQuery(GET_PRICING_OFFER, { variables: { id } });

    useEffect(() => {
        if (getTerminalPricing.data) {
            const primaryProducts = getTerminalPricing?.data.offer.primaryProducts;
            const primaryProductIds = primaryProducts.map(({ id }) => {
                return id;
            });
            setPrimaryProductList(primaryProducts);
            setSelectedPrimaryProductIds(primaryProductIds);

            const secondaryProducts = getTerminalPricing?.data.offer.secondaryProducts;
            const secondaryProductIds = secondaryProducts.map(({ id }) => {
                return id;
            });
            setSecondaryProductList(secondaryProducts);
            setSelectedSecondaryProductIds(secondaryProductIds);

            const { partners } = getTerminalPricing.data.offer;
            const selectedPartners = partners.map(({ id, firstName, lastName, business }) => {
                return {
                    id,
                    name: business?.legalName || `${firstName} ${lastName}`,
                };
            });
            setSelectedPartners(selectedPartners);

            const { pricings, promos } = getTerminalPricing.data.offer;
            const selectedProducts = [...primaryProducts, ...secondaryProducts];

            const selectedProductsWithPricing = selectedProducts.reduce((products, product) => {
                const selectedPromo = promos.find(({ productId }) => {
                    return productId === product.id;
                });
                const pricing = pricings.find((pricing: any) => {
                    return pricing.product.id === product.id;
                });

                let shouldApplyPromo = SalesConditionsType.NO;
                if (selectedPromo?.applyPromo) {
                    const { applyPromo } = selectedPromo;
                    shouldApplyPromo = applyPromo
                        ? SalesConditionsType.YES
                        : SalesConditionsType.NO;
                }
                products = [
                    ...products,
                    {
                        ...product,
                        shouldApplyPromo,
                        pricing: { ...pricing.prices, productId: product.id },
                    },
                ];
                return products;
            }, []);

            setSelectedProducts(selectedProductsWithPricing);
            setDefaultProducts(selectedProductsWithPricing);
        }
    }, [getTerminalPricing?.data]);

    const handleOpenSaveModal = (e: any, props: FormikValues) => {
        e.stopPropagation();
        const { status } = props;
        if (status === OfferStatus.ACTIVE || status === OfferStatus.DEACTIVATED) {
            setOpenSaveModal(true);
            setFormValues(props);
            return;
        }
        if (status === OfferStatus.DELETED) {
            onSubmit(props);
        }
    };

    const handleCloseSaveModal = () => {
        setOpenSaveModal(false);
    };

    const handleSaveRecord = () => {
        onSubmit(formValues);
        handleCloseSaveModal();
    };

    const handleSetOfferStatus = (value: string) => {
        if (value === OfferStatus.DEACTIVATED || value === OfferStatus.DELETED) {
            setShowEditProduct(false);
            setDisabledOfferName(true);
        } else {
            setShowEditProduct(true);
            setDisabledOfferName(false);
        }
    };

    const viewPricingOfferById = async (id: number) => {
        const pricingOffer = await getTerminalPricingOffer({ variables: { id } });
        if (pricingOffer?.data?.offer) {
            const {
                data: { offer },
            } = pricingOffer;
            setSelectedOffer(offer);
        }
    };

    useEffect(() => {
        refetch({
            input: {
                ...filterInputs,
                type: pricingType,
            },
        });
    }, [paginationModel, refetch]);

    return {
        paginationModel,
        setPaginationModel,
        primaryProduct,
        setPrimaryProduct,
        primaryProductList,
        selectedPrimaryProductIds,
        secondaryProductList,
        selectedSecondaryProductIds,
        selectedProducts,
        selectedPartners,
        setSelectedPartners,
        errorMessage,
        open,
        expanded,
        showCustomTerm,
        onSubmit,
        initialValues,
        validationSchema,
        loadingCreatePricingOffer,
        loadingUpdatePricingOffer,
        handleSetOfferStatus,
        handleOpenSaveModal,
        handleSaveRecord,
        updatePricing,
        handlePricingTypeOnChange,
        enableProductForPromo,
        handleSetSelectContractTerm,
        handleFindPrimaryProducts,
        handleSelectedPrimaryProductsIds,
        handleSelectedSecondaryProductsIds,
        handleAccordionChange,
        handlePricingOnChange,
        handleMultiplePricingOnChange,
        handleCloseModal,
        handleOpenModal,
        getPricingOfferById,
        rowCountState,
        setRowCountState,
        mapPageToNextCursor,
        handlePaginationModelChange,
        handlePricingLimitChange,
        openSaveModal,
        disabledOfferName,
        showEditProduct,
        formValues,
        handleCloseSaveModal,
        getTerminalPricing,
        setOpenModal,
        selectedOffer,
        viewPricingOfferById,
        offerData: data ? data.getPricingOffersByType.data : [],
        updateSelectedSecondaryProductIds,
        updateSelectedPrimaryProductIds,
    };
};

export default usePricingsOffers;
